/** Anzeigeformate. Eine Stelle, damit die Zahlen überall gleich aussehen. */

const numberFormat = new Intl.NumberFormat('de-CH')

/** Ganze Zahl mit Tausendertrennung, etwa 4230 → "4'230". */
export function num(value: number): string {
  return numberFormat.format(Math.round(value))
}

const currencyCache = new Map<string, Intl.NumberFormat>()

/** Zahlen für eine Währung, die Intl nicht kennt: Betrag und Code daneben. */
const plainMoney = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Betrag mit Währung, etwa 42.5 / EUR → "42.50 €".
 *
 * Ein unbekannter Code wirft nicht, sondern setzt sich als Wort hinter die
 * Zahl. Seit die Währung im Reiter Daten eingetippt werden kann, geht durch
 * dieses Feld jeder Zwischenstand einer Eingabe - und Intl beantwortet ein
 * halb getipptes "CH" mit einer Ausnahme, die die ganze Budgetansicht mitnähme.
 * Aus dem Abgleich und aus einer Importdatei kommt ohnehin, was dort steht.
 */
export function money(value: number, currency: string): string {
  let fmt = currencyCache.get(currency)
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      })
    } catch {
      return `${plainMoney.format(value)}${currency ? ` ${currency}` : ''}`
    }
    currencyCache.set(currency, fmt)
  }
  return fmt.format(value)
}

/** Stunden mit höchstens einer Nachkommastelle, etwa 6.5 → "6.5". */
export function hours(value: number): string {
  return String(Math.round(value * 10) / 10)
}

/** Tagesnummer zweistellig, etwa 3 → "03". */
export function dayLabel(day: number): string {
  return String(day).padStart(2, '0')
}

/** Anzahl mit passender Zahlform, etwa 1 → "1 Etappe", 16 → "16 Etappen". */
export function anzahl(wert: number, einzahl: string, mehrzahl: string): string {
  return `${num(wert)} ${wert === 1 ? einzahl : mehrzahl}`
}

/**
 * Angaben nebeneinander, durch einen Mittelpunkt getrennt - und Leeres fällt
 * weg.
 *
 * Das Weglassen ist der ganze Zweck. Solange jede Reise mit der ausgefüllten
 * Vorlage begann, standen in diesen Zeilen immer alle Angaben; seit eine neue
 * Reise leer ist, stünde dort sonst " · 0 Etappen · Start " - ein
 * Trennpunkt vor dem Nichts und ein "Start" ohne Datum, im Kopf der App wie
 * auf dem gedruckten Umschlag.
 */
export function zeile(teile: (string | false | null | undefined)[]): string {
  return teile.filter((teil): teil is string => Boolean(teil)).join(' · ')
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * ISO-Datum eines Zeitpunkts, YYYY-MM-DD, in lokaler Zeit.
 *
 * Ausdrücklich lokal und nicht über toISOString: die Zeitstempel eines
 * Tracks sind UTC, und eine Fahrt, die um 00:30 Ortszeit endet, gehört in
 * Mitteleuropa trotzdem zum Vortag - dem Tag, den der Nutzer im Tagebuch
 * abhakt.
 */
export function isoDate(ts: number): string {
  const d = new Date(ts)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** ISO-Datum von heute, YYYY-MM-DD, in lokaler Zeit. */
export function today(): string {
  return isoDate(Date.now())
}

/** Uhrzeit eines Zeitpunkts, etwa "08:35", in lokaler Zeit. */
export function clock(ts: number): string {
  const d = new Date(ts)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Dauer aus Sekunden, etwa 4520 → "1 h 15 min".
 *
 * Gerundet auf Minuten: bei einer Fahrzeit von sechs Stunden ist die Sekunde
 * eine Scheingenauigkeit. Unter einer Stunde bleiben nur die Minuten stehen,
 * sonst stände dort "0 h 12 min".
 */
export function duration(seconds: number): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds / 60)) : 0
  const h = Math.floor(total / 60)
  const min = total % 60
  return h > 0 ? `${String(h)} h ${pad(min)} min` : `${String(min)} min`
}

/** Zeitpunkt als kurze, lesbare Angabe für die Statusleiste. */
export function timeAgo(ts: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ts) / 1000))
  if (s < 45) return 'gerade eben'
  const m = Math.round(s / 60)
  if (m < 60) return `vor ${m} Min.`
  const h = Math.round(m / 60)
  if (h < 24) return `vor ${h} Std.`
  return `vor ${Math.round(h / 24)} Tagen`
}
